import { Alert, Box, Button, Chip, Typography, CircularProgress } from '@mui/material';
import RegisteredIcon from '@mui/icons-material/HowToReg';
import LockIcon from '@mui/icons-material/Lock';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddSeatsIcon from '@mui/icons-material/AddCircleOutline';

interface TenantUpgradeCtaProps {
  hasPendingRequest: boolean;
  loading: boolean;
  success: boolean;
  onRequest: () => void;
  // Admin-mode props (all optional, backward-compatible)
  isAdmin?: boolean;
  seatAvailable?: boolean | null;
  activating?: boolean;
  activated?: boolean;
  activateError?: string | null;
  onActivate?: () => void;
  onManageBilling?: () => void;
  moduleLabel?: string;
}

export default function TenantUpgradeCta({
  hasPendingRequest,
  loading,
  success,
  onRequest,
  isAdmin,
  seatAvailable,
  activating,
  activated,
  activateError,
  onActivate,
  onManageBilling,
  moduleLabel,
}: TenantUpgradeCtaProps) {
  // --- Admin variant ---
  if (isAdmin) {
    if (activated) {
      return (
        <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
          Access activated. You can now register for courses in this module.
        </Alert>
      );
    }

    if (seatAvailable === null) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (seatAvailable) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<AdminIcon sx={{ fontSize: 16 }} />}
              label="Admin"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '12px' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            As an admin, you can activate {moduleLabel ?? 'module'} access for yourself.
          </Typography>
          {activateError && <Alert severity="error" sx={{ py: 0.5 }}>{activateError}</Alert>}
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={activating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            onClick={onActivate}
            disabled={activating}
            sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
          >
            {activating ? 'Activating…' : 'Activate Access'}
          </Button>
        </Box>
      );
    }

    // No seats available
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<AdminIcon sx={{ fontSize: 16 }} />}
            label="Admin"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '12px' }}
          />
        </Box>
        <Typography variant="body2" color="text.disabled">
          No available {moduleLabel ?? 'module'} seats
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<AddSeatsIcon />}
          onClick={onManageBilling}
          sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
        >
          Add Seats
        </Button>
      </Box>
    );
  }

  // --- Regular user variant (unchanged) ---
  if (success || hasPendingRequest) {
    return (
      <Alert severity="success" icon={<RegisteredIcon fontSize="inherit" />}>
        Your request has been sent to your administrator. You&apos;ll be notified once access is granted.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled' }}>
        <LockIcon sx={{ fontSize: 20 }} />
        <Typography variant="body2" color="text.disabled">
          Module access required
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        You don&apos;t have a seat for this module. Request access from your organization administrator.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RegisteredIcon />}
        onClick={onRequest}
        disabled={loading}
        sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
      >
        {loading ? 'Requesting…' : 'Request Access'}
      </Button>
    </Box>
  );
}
